import React from "react";
import PropTypes from "prop-types";
import moment from "moment";

import { UIFormSectionCard } from "../../../../ui";
import SummaryRow from "./SummaryRow";
import {
  fillTemplate,
  formatAmount,
  formatCrewSizePrice,
  getCrewSizeFee,
  getEventBillingModel,
  getOperatingDays,
} from "../ChooseCrewSize/crewSizeUtils";

const formatDate = (value) =>
  value ? moment(value).locale("en").format("DD-MM-YYYY") : "";

const EventSummaryCard = ({
  t,
  crewSizeT,
  eventName,
  startAt,
  endAt,
  event,
  crewSizeOption,
  planName,
}) => {
  const operatingDays = event?.operating_days ?? getOperatingDays(startAt, endAt);
  const model = getEventBillingModel(event, operatingDays);

  // The total and the size's fee are the backend's (PROLINE-3924, PROLINE-4162); this step prices
  // nothing itself, so a total the backend hasn't returned shows as "—". A custom plan carries its own rate.
  let rate = null;
  let amount = null;
  let ratePlanName = planName;
  if (crewSizeOption?.custom) {
    rate = crewSizeOption.rate;
    amount = crewSizeOption.cost;
    ratePlanName = crewSizeOption.name ?? crewSizeT.sizes.custom;
  } else if (crewSizeOption) {
    rate = getCrewSizeFee(crewSizeOption, model);
    amount = crewSizeOption.cost;
  }

  return (
    <UIFormSectionCard title={t.eventSummary} className="publish-pay-step__card publish-pay-step__card--rows">
      <SummaryRow label={t.event} value={eventName || null} />
      <SummaryRow
        label={t.dates}
        value={
          startAt && endAt
            ? fillTemplate(t.datesValue, {
                start: formatDate(startAt),
                end: formatDate(endAt),
                days: formatAmount(operatingDays),
              })
            : null
        }
      />
      <SummaryRow
        label={t.crewSize}
        value={
          crewSizeOption
            ? fillTemplate(t.crewSizeValue, {
                size: crewSizeOption.name ?? crewSizeT.sizes[crewSizeOption.key],
                max: formatAmount(crewSizeOption.max),
              })
            : null
        }
      />
      <SummaryRow
        label={t.appliedRate}
        value={formatCrewSizePrice(crewSizeT, rate, model, ratePlanName)}
      />
      <SummaryRow
        total
        label={t.totalAmountDue}
        value={
          amount != null ? `${formatAmount(amount)} ${crewSizeT.currency}` : null
        }
      />
    </UIFormSectionCard>
  );
};

EventSummaryCard.propTypes = {
  t: PropTypes.object.isRequired,
  crewSizeT: PropTypes.object.isRequired,
  eventName: PropTypes.string,
  startAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  endAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** The saved event: its `operating_days` and `payment_period` drive the billing model. */
  event: PropTypes.object,
  crewSizeOption: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    max: PropTypes.number,
    /** Fees of this size per day / flat, as priced by the backend. */
    dailyFee: PropTypes.number,
    monthlyFee: PropTypes.number,
    /** Fee of this size for the event, as priced by the backend. */
    cost: PropTypes.number,
    /** Custom plan: carries its own agreed rate. */
    custom: PropTypes.bool,
    rate: PropTypes.number,
  }),
  /** Subscription plan of the company, e.g. "Basic". */
  planName: PropTypes.string,
};

EventSummaryCard.defaultProps = {
  eventName: "",
  startAt: undefined,
  endAt: undefined,
  event: undefined,
  crewSizeOption: null,
  planName: "",
};

export default EventSummaryCard;
